import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import WebViewer from '@pdftron/webviewer';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { GetAuthenticatedDocUrl } from '../../redux/documentsSlice';
import { isAudioFile } from '../../utils/commonUtils';
import { runCitationHighlightAsync } from './pdfTronCitationHighlight';
import styles from './css/index.module.css';

function getFileExtension(file) {
    if (file?.document) {
        const parts = file.document.split('.');
        return parts.length > 1 ? parts.pop().toLowerCase() : null;
    }
    return null;
}

function isPDFTronSupportedFile(file) {
    const extension = getFileExtension(file);
    if (!extension) return false;
    const supportedExtensions = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xlsx', 'xls', 'csv', 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'txt', 'rtf'];
    return supportedExtensions.includes(extension);
}

/**
 * Half-height / side PDFTron embed for AI Warden citation preview.
 */
const AiWardenPdfPanel = ({ file, highlightText, pageOneBased, onClose, headerKicker = 'PDF viewer', closeAriaLabel = 'Close citation viewer' }) => {
    const dispatch = useDispatch();
    const viewerRef = useRef(null);
    const instanceRef = useRef(null);
    const highlightPendingRef = useRef({ text: highlightText || '', page: pageOneBased ?? null });
    const [fileUrl, setFileUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        highlightPendingRef.current = { text: highlightText || '', page: pageOneBased ?? null };
    }, [highlightText, pageOneBased, file?.id]);

    const getExtension = useCallback(() => getFileExtension(file), [file]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setError(null);
            setPdfLoading(true);
            setFileUrl(null);
            instanceRef.current = null;
            if (!file?.document) {
                setPdfLoading(false);
                setError('No file URL for this document.');
                return;
            }
            if (!isPDFTronSupportedFile(file)) {
                setPdfLoading(false);
                setError('This file type cannot be previewed here.');
                return;
            }
            const documentUrl = file.document;
            try {
                if (documentUrl.includes('.blob.core.windows.net')) {
                    const authenticatedUrl = await dispatch(GetAuthenticatedDocUrl(documentUrl)).unwrap();
                    if (!cancelled) setFileUrl(authenticatedUrl);
                } else if (!isAudioFile(documentUrl)) {
                    if (!cancelled) setFileUrl(documentUrl);
                } else {
                    if (!cancelled) setError('Audio preview is not supported in this panel.');
                }
            } catch (e) {
                if (!cancelled) {
                    setError('Could not load a secure link for this file.');
                    setPdfLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [file, dispatch]);

    useEffect(() => {
        if (!fileUrl || !viewerRef.current) return undefined;
        if (!isPDFTronSupportedFile(file)) return undefined;

        if (instanceRef.current) {
            instanceRef.current = null;
        }

        setPdfLoading(true);
        const fileExtension = getExtension() || 'pdf';

        let disposed = false;
        WebViewer.Iframe(
            {
                path: '/lib/webviewer',
                initialDoc: fileUrl,
                extension: fileExtension || 'pdf',
                webviewerServerURL: process.env.REACT_APP_PDF_WEBVIEWER_URL,
                licenseKey: process.env.REACT_APP_PDF_EXPRESS_KEY,
            },
            viewerRef.current,
        )
            .then(instance => {
                if (disposed) return;
                instanceRef.current = instance;
                const { Core } = instance;
                Core.documentViewer.addEventListener('documentLoadError', err => {
                    console.error('AI Warden PDF panel load error:', err);
                    setPdfLoading(false);
                    setError('Failed to open this document in the viewer.');
                });

                Core.documentViewer.addEventListener('documentLoaded', () => {
                    if (disposed) return;
                    setPdfLoading(false);
                    instance.UI.disableElements(['toolsHeader', 'ribbons', 'toggleNotesButton', 'menuButton']);

                    const pending = highlightPendingRef.current;
                    const text = pending?.text || '';
                    window.setTimeout(() => {
                        if (disposed || !instanceRef.current) return;
                        void runCitationHighlightAsync(instanceRef.current, text, {
                            pageOneBased: pending?.page,
                        }).catch(err => console.warn('Citation highlight:', err));
                    }, 450);
                });
            })
            .catch(err => {
                console.error('PDFTron init (AI Warden):', err);
                if (!disposed) {
                    setPdfLoading(false);
                    setError('Viewer failed to start.');
                }
            });

        return () => {
            disposed = true;
            instanceRef.current = null;
        };
    }, [fileUrl, file, getExtension]);

    return (
        <div className={styles.aiWardenPdfColumn}>
            <div className={styles.aiWardenPdfHeader}>
                <div className={styles.aiWardenPdfTitleWrap}>
                    <span className={styles.aiWardenKicker}>{headerKicker}</span>
                    <span className={styles.aiWardenPdfTitle} title={file?.document_name || file?.name}>
                        {file?.document_name || file?.name || `File ${file?.id}`}
                    </span>
                </div>
                <button type="button" className={styles.aiWardenPdfClose} onClick={onClose} aria-label={closeAriaLabel}>
                    <Dismiss20Regular />
                </button>
            </div>
            <div className={styles.aiWardenPdfBody}>
                {error && <p className={styles.aiWardenPdfError}>{error}</p>}
                {!error && (
                    <div key={file?.id || 'wv'} className={`webviewer ${styles.aiWardenPdfViewport}`} ref={viewerRef}>
                        {pdfLoading && (
                            <div className={styles.aiWardenPdfLoading}>
                                <AiOutlineLoading3Quarters className="rotateLoader" size={36} />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AiWardenPdfPanel;
